import React, { useState } from 'react';

interface JobListing {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
}

const CareersPage: React.FC = () => {
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All Departments');
  const [selectedLocation, setSelectedLocation] = useState<string>('All Locations');
  
  const jobListings: JobListing[] = [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      department: 'Engineering',
      location: 'San Francisco, CA (Remote Option)',
      type: 'Full-time'
    },
    {
      id: 2,
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Los Angeles, CA',
      type: 'Full-time'
    },
    {
      id: 3,
      title: 'Content Acquisition Manager',
      department: 'Content',
      location: 'New York, NY',
      type: 'Full-time'
    },
    {
      id: 4,
      title: 'Marketing Specialist',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time'
    },
    {
      id: 5,
      title: 'Data Scientist',
      department: 'Analytics',
      location: 'Chicago, IL (Remote Option)',
      type: 'Full-time'
    },
    {
      id: 6,
      title: 'Customer Support Representative',
      department: 'Support',
      location: 'Remote',
      type: 'Part-time / Full-time'
    }
  ];

  const departments = ['All Departments', 'Engineering', 'Design', 'Content', 'Marketing', 'Analytics', 'Support'];
  const locations = ['All Locations', 'San Francisco, CA', 'Los Angeles, CA', 'New York, NY', 'Chicago, IL', 'Remote'];

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDepartment(e.target.value);
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLocation(e.target.value);
  };

  const filteredJobs = jobListings.filter(job => {
    const matchesDepartment = selectedDepartment === 'All Departments' || job.department === selectedDepartment;
    const matchesLocation = selectedLocation === 'All Locations' || 
      job.location.includes(selectedLocation) || 
      (selectedLocation === 'Remote' && job.location.includes('Remote'));
    return matchesDepartment && matchesLocation;
  });

  return (
    <main>
      <div className="container">
        <div style={{ marginTop: '120px', marginBottom: '50px' }}>
          <h1 className="section-title">Join Our Team</h1>
          
          <div style={{ marginTop: '30px' }}>
            <div style={{ 
              background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '8px',
              padding: '60px 40px',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Work With Us</h2>
              <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto 30px', lineHeight: '1.6' }}>
                At Quick Flick, we're building the future of entertainment. Join our diverse team of passionate 
                individuals who are committed to delivering exceptional streaming experiences.
              </p>
              <a href="#openings" className="btn btn-primary">View Open Positions</a>
            </div>
            
            <h2 style={{ fontSize: '1.5rem', margin: '30px 0 20px' }}>Why Work at Quick Flick?</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '40px' }}>
              {[
                { title: 'Competitive Compensation', description: 'Salary packages that recognize your skills and experience' },
                { title: 'Health & Wellness', description: 'Comprehensive benefits, mental health resources, and wellness programs' },
                { title: 'Work-Life Balance', description: 'Flexible scheduling, generous PTO, and remote work options' },
                { title: 'Growth Opportunities', description: 'Professional development, mentorship, and career advancement' },
                { title: 'Inclusive Culture', description: 'Diverse and inclusive workplace where all voices are valued' },
                { title: 'Perks & Benefits', description: 'Free subscription, team events, and employee discounts' }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  style={{ 
                    flex: '1 1 300px',
                    background: 'var(--secondary-color)',
                    padding: '20px',
                    borderRadius: '8px'
                  }}
                >
                  <h3 style={{ color: 'var(--primary-color)', marginBottom: '10px' }}>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
            
            <div id="openings">
              <h2 style={{ fontSize: '1.5rem', margin: '30px 0 20px' }}>Open Positions</h2>
              
              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Department</label>
                  <select 
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    style={{ 
                      width: '100%', 
                      padding: '10px', 
                      backgroundColor: 'var(--secondary-color)',
                      color: 'var(--text-color)',
                      border: 'none',
                      borderRadius: '4px'
                    }}
                  >
                    {departments.map((dept, index) => (
                      <option key={index} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Location</label>
                  <select 
                    value={selectedLocation}
                    onChange={handleLocationChange}
                    style={{ 
                      width: '100%', 
                      padding: '10px', 
                      backgroundColor: 'var(--secondary-color)',
                      color: 'var(--text-color)',
                      border: 'none',
                      borderRadius: '4px'
                    }}
                  >
                    {locations.map((loc, index) => (
                      <option key={index} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: '1 1 200px', display: 'flex', alignItems: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      setSelectedDepartment('All Departments');
                      setSelectedLocation('All Locations');
                    }}
                    className="btn btn-secondary" 
                    style={{ width: '100%' }}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
              
              {filteredJobs.length === 0 ? (
                <div 
                  style={{ 
                    background: 'var(--secondary-color)',
                    borderRadius: '8px',
                    padding: '30px',
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}
                >
                  <h3 style={{ marginBottom: '10px' }}>No matching positions found</h3>
                  <p>Try adjusting your filters or check back later for new opportunities.</p>
                </div>
              ) : (
                <div>
                  {filteredJobs.map(job => (
                    <div 
                      key={job.id}
                      style={{ 
                        background: 'var(--secondary-color)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '15px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <h3 style={{ marginBottom: '5px' }}>{job.title}</h3>
                          <div style={{ color: 'var(--light-gray)' }}>
                            <span>{job.department}</span> • <span>{job.location}</span> • <span>{job.type}</span>
                          </div>
                        </div>
                        <a 
                          href="/contact" 
                          className="btn btn-primary"
                          style={{ alignSelf: 'center' }}
                        >
                          View Job
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ 
              background: 'var(--secondary-color)',
              padding: '30px',
              borderRadius: '8px',
              marginTop: '40px',
              textAlign: 'center'
            }}>
              <h2 style={{ marginBottom: '15px' }}>Don't See the Right Position?</h2>
              <p style={{ marginBottom: '20px', maxWidth: '700px', margin: '0 auto 20px' }}>
                We're always looking for talented individuals to join our team. Send us your resume, and we'll keep it on file for future opportunities.
              </p>
              <a href="/contact" className="btn btn-primary">Send Your Resume</a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CareersPage; 